import React, {Fragment} from 'react';
import {Badge, Button, Card, Col, Container, Fade, Form, Row, Table} from 'react-bootstrap';
import {useSelector} from 'react-redux';
import {FaBoxOpen, FaClipboardList, FaCog, FaExclamationTriangle, FaPlus, FaShippingFast} from 'react-icons/fa';
import {
  createAdminShopProduct,
  createAdminShopVariant,
  createPartnerShopProduct,
  createPartnerShopVariant,
  deleteAdminShopProduct,
  deleteAdminShopProductImage,
  deleteAdminShopVariant,
  deletePartnerShopProduct,
  deletePartnerShopProductImage,
  deletePartnerShopVariant,
  getAdminShopOrders,
  getAdminShopProducts,
  getAdminShopSettings,
  getPartnerShopOrders,
  getPartnerShopProducts,
  getPartnerShopSettings,
  updateAdminShopOrder,
  updateAdminShopProduct,
  updateAdminShopSettings,
  updateAdminShopVariant,
  updatePartnerShopOrder,
  updatePartnerShopProduct,
  updatePartnerShopSettings,
  updatePartnerShopVariant,
  uploadAdminShopProductImages,
  uploadPartnerShopProductImages
} from '../api';
import CommonMetaTag from './common-meta-tag';
import Configuration from '../config';
import {useTranslation} from 'react-i18next';

const emptyProduct = {
  name: '', sku: '', category: '', price: '', salePrice: '',
  status: 'draft', weightGrams: '', description: ''
};
const emptyVariant = {
  size: '', color: '', sku: '', stockQuantity: '0', price: '', active: true
};
const newEmptyVariant = () => ({
  ...emptyVariant,
  sku: ''
});
const emptySettings = {
  order_notification_email: '', shipping_enabled: true, shipping_cost_cents: 0,
  free_shipping_threshold_cents: null, pickup_enabled: false
};

const cents = value => Math.round(Number(String(value).replace(',', '.')) * 100);
const euros = value => value == null ? '' : (value / 100).toFixed(2);
const optional = value => String(value == null ? '' : value).trim() || null;
const normalizeSkuPart = value => {
  const raw = String(value == null ? '' : value).trim();
  if (!raw) return '';
  return raw
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toUpperCase();
};
const buildVariantSku = (productSku, size, color) => {
  const parts = [normalizeSkuPart(productSku), normalizeSkuPart(size), normalizeSkuPart(color)].filter(Boolean);
  return parts.join('-');
};
const orderStatusKeys = {
  paid: 'SHOP_ORDER_PAID',
  preparing: 'SHOP_ORDER_PREPARING',
  shipped: 'SHOP_ORDER_SHIPPED',
  delivered: 'SHOP_ORDER_DELIVERED',
  cancelled: 'SHOP_ORDER_CANCELLED'
};
const productStatusKeys = {
  draft: 'SHOP_PRODUCT_DRAFT',
  published: 'SHOP_PRODUCT_PUBLISHED',
  sold_out: 'SHOP_PRODUCT_SOLD_OUT',
  archived: 'SHOP_PRODUCT_ARCHIVED'
};
const statusBadgeVariant = status => ({
  published: 'success',
  paid: 'warning',
  preparing: 'info',
  shipped: 'primary',
  delivered: 'success',
  sold_out: 'danger',
  cancelled: 'secondary',
  archived: 'secondary',
  draft: 'light'
}[status] || 'secondary');
const orderDeliveryLine = order => {
  const parts = [
    order.delivery_address,
    [order.delivery_cap, order.delivery_city, order.delivery_province].filter(Boolean).join(' ')
  ].filter(Boolean);
  return parts.join(', ');
};
const orderDeliveryBlock = (order, t) => [
  order.delivery_name,
  order.delivery_phone,
  order.delivery_address,
  [order.delivery_cap, order.delivery_city, order.delivery_province].filter(Boolean).join(' '),
  order.delivery_country,
  order.delivery_notes ? `${t('NOTES')}: ${order.delivery_notes}` : null
].filter(Boolean);

const AdminShop = ({scope = 'admin', partnerId = null, title = 'Shop Admin', ordersTitle = 'Ordini Shop Replayer'}) => {
  const {t, i18n} = useTranslation();
  const orderStatusLabel = status => orderStatusKeys[status] ? t(orderStatusKeys[status]) : status;
  const productStatusLabel = status => productStatusKeys[status] ? t(productStatusKeys[status]) : status;
  const displayedOrdersTitle = ordersTitle === 'Ordini Shop Replayer' ? t('SHOP_REPLAYER_ORDERS') : ordersTitle;
  const token = useSelector(state => state.login && state.login.token);
  const isPartnerScope = scope === 'partner';
  const scopeOptions = React.useMemo(() => partnerId ? {partnerId} : {}, [partnerId]);
  const [products, setProducts] = React.useState([]);
  const [product, setProduct] = React.useState(emptyProduct);
  const [editingProductId, setEditingProductId] = React.useState(null);
  const [variant, setVariant] = React.useState(emptyVariant);
  const [initialVariants, setInitialVariants] = React.useState([newEmptyVariant()]);
  const [editingVariantId, setEditingVariantId] = React.useState(null);
  const [pendingImages, setPendingImages] = React.useState([]);
  const [pendingImagePreviews, setPendingImagePreviews] = React.useState([]);
  const [imageVariantSku, setImageVariantSku] = React.useState('');
  const [imageLabel, setImageLabel] = React.useState('');
  const [imageInputKey, setImageInputKey] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [orders, setOrders] = React.useState([]);
  const [orderFilter, setOrderFilter] = React.useState('');
  const [selectedOrderId, setSelectedOrderId] = React.useState(null);
  const [orderDraft, setOrderDraft] = React.useState({
    status: 'paid', tracking_code: '', internal_note: '', note: ''
  });
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [shopSection, setShopSection] = React.useState('products');
  const [settings, setSettings] = React.useState(emptySettings);
  const [settingsDraft, setSettingsDraft] = React.useState({
    orderNotificationEmail: '', shippingEnabled: true, shippingCost: '0.00',
    freeShippingThreshold: '', pickupEnabled: false
  });
  const [settingsLoading, setSettingsLoading] = React.useState(false);
  const [productEditorOpen, setProductEditorOpen] = React.useState(false);
  const imageSectionRef = React.useRef(null);
  const imageInputRef = React.useRef(null);

  const selectedProduct = products.find(item => item.id === editingProductId);
  const selectedProductImages = selectedProduct ? (selectedProduct.images || []) : [];
  const currentImageVariantOptions = editingProductId
    ? (selectedProduct ? (selectedProduct.variants || []) : [])
    : initialVariants.filter(item => item.sku.trim());

  const resolveVariantId = (variants, sku) => {
    if (!sku) return null;
    const match = variants.find(item => item.sku === sku);
    return match ? match.id : null;
  };

  React.useEffect(() => {
    return () => {
      pendingImagePreviews.forEach(item => URL.revokeObjectURL(item.url));
    };
  }, [pendingImagePreviews]);

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const response = isPartnerScope
      ? await getPartnerShopProducts(token)
      : await getAdminShopProducts(token, scopeOptions);
    if (response.status === 200) {
      setProducts(response.json);
      setMessage('');
    } else {
      setMessage(response.json.error || t('SHOP_PRODUCTS_LOAD_ERROR'));
    }
    setLoading(false);
  }, [token, isPartnerScope, scopeOptions, t]);

  React.useEffect(() => { load(); }, [load]);

  const loadOrders = React.useCallback(async () => {
    if (!token) return;
    setOrdersLoading(true);
    const response = isPartnerScope
      ? await getPartnerShopOrders(token, orderFilter)
      : await getAdminShopOrders(token, orderFilter, scopeOptions);
    if (response.status === 200) {
      setOrders(response.json);
    } else {
      setMessage(response.json.error || t('SHOP_ORDERS_LOAD_ERROR'));
    }
    setOrdersLoading(false);
  }, [token, orderFilter, isPartnerScope, scopeOptions, t]);

  React.useEffect(() => { loadOrders(); }, [loadOrders]);

  const loadSettings = React.useCallback(async () => {
    if (!token) return;
    setSettingsLoading(true);
    const response = isPartnerScope
      ? await getPartnerShopSettings(token)
      : await getAdminShopSettings(token, scopeOptions);
    if (response.status === 200) {
      const value = {...emptySettings, ...response.json};
      setSettings(value);
      setSettingsDraft({
        orderNotificationEmail: value.order_notification_email || '',
        shippingEnabled: Boolean(value.shipping_enabled),
        shippingCost: euros(value.shipping_cost_cents),
        freeShippingThreshold: value.free_shipping_threshold_cents == null ? '' : euros(value.free_shipping_threshold_cents),
        pickupEnabled: Boolean(value.pickup_enabled)
      });
    } else {
      setMessage(response.json.error || t('SHOP_SETTINGS_LOAD_ERROR'));
    }
    setSettingsLoading(false);
  }, [token, isPartnerScope, scopeOptions, t]);

  React.useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveSettings = async event => {
    event.preventDefault();
    if (!settingsDraft.shippingEnabled && !settingsDraft.pickupEnabled) {
      setMessage(t('SHOP_DELIVERY_METHOD_REQUIRED'));
      return;
    }
    const shippingCost = cents(settingsDraft.shippingCost || 0);
    const threshold = settingsDraft.freeShippingThreshold === ''
      ? null
      : cents(settingsDraft.freeShippingThreshold);
    if (!Number.isFinite(shippingCost) || shippingCost < 0 ||
      (threshold !== null && (!Number.isFinite(threshold) || threshold < 0))) {
      setMessage(t('SHOP_CHECK_SHIPPING_COSTS'));
      return;
    }
    setSaving(true);
    const payload = {
      order_notification_email: optional(settingsDraft.orderNotificationEmail),
      shipping_enabled: settingsDraft.shippingEnabled,
      shipping_cost_cents: shippingCost,
      free_shipping_threshold_cents: threshold,
      pickup_enabled: settingsDraft.pickupEnabled
    };
    const response = isPartnerScope
      ? await updatePartnerShopSettings(token, payload)
      : await updateAdminShopSettings(token, payload, scopeOptions);
    if (response.status === 200) {
      setSettings(response.json);
      setMessage(t('SHOP_SETTINGS_SAVED'));
      await loadSettings();
    } else {
      setMessage(response.json.error || t('SHOP_SETTINGS_SAVE_ERROR'));
    }
    setSaving(false);
  };

  const selectOrder = item => {
    setSelectedOrderId(item.id);
    setOrderDraft({
      status: item.status,
      tracking_code: item.tracking_code || '',
      internal_note: item.internal_note || '',
      note: ''
    });
  };

  const saveOrder = async event => {
    event.preventDefault();
    if (!selectedOrderId) return;
    setSaving(true);
    const response = isPartnerScope
      ? await updatePartnerShopOrder(token, selectedOrderId, orderDraft)
      : await updateAdminShopOrder(token, selectedOrderId, orderDraft, scopeOptions);
    if (response.status === 200) {
      setMessage(t('SHOP_ORDER_UPDATED', {number: response.json.number}));
      await loadOrders();
      selectOrder(response.json);
    } else {
      setMessage(response.json.error || t('SHOP_ORDER_UPDATE_ERROR'));
    }
    setSaving(false);
  };

  const resetProduct = () => {
    setProduct(emptyProduct);
    setEditingProductId(null);
    setVariant(emptyVariant);
    setInitialVariants([newEmptyVariant()]);
    setEditingVariantId(null);
    setPendingImages([]);
    setPendingImagePreviews([]);
    setImageVariantSku('');
    setImageLabel('');
    setImageInputKey(value => value + 1);
    setProductEditorOpen(false);
  };

  const startNewProduct = () => {
    resetProduct();
    setMessage('');
    setShopSection('products');
    setProductEditorOpen(true);
    window.scrollTo(0, 0);
  };

  const editProduct = item => {
    setEditingProductId(item.id);
    setProduct({
      name: item.name || '',
      sku: item.sku || '',
      category: item.category || '',
      price: euros(item.price_cents),
      salePrice: euros(item.sale_price_cents),
      status: item.status || 'draft',
      weightGrams: item.weight_grams == null ? '' : item.weight_grams,
      description: item.description || ''
    });
    setVariant({
      ...emptyVariant,
      sku: buildVariantSku(item.sku, item.size, item.color) || item.sku || ''
    });
    setEditingVariantId(null);
    setPendingImages([]);
    setPendingImagePreviews([]);
    setImageVariantSku('');
    setImageLabel('');
    setImageInputKey(value => value + 1);
    setShopSection('products');
    setProductEditorOpen(true);
    window.scrollTo(0, 0);
  };

  const saveProduct = async event => {
    event.preventDefault();
    setSaving(true);
    const payload = {
      name: product.name.trim(),
      sku: product.sku.trim(),
      category: optional(product.category),
      price_cents: cents(product.price),
      sale_price_cents: product.salePrice === '' ? null : cents(product.salePrice),
      status: product.status,
      weight_grams: product.weightGrams === '' ? null : Number(product.weightGrams),
      description: optional(product.description),
      variants: editingProductId ? undefined : initialVariants
        .filter(item => item.sku.trim())
        .map(item => ({
          size: optional(item.size),
          color: optional(item.color),
          sku: buildVariantSku(product.sku, item.size, item.color) || item.sku.trim(),
          stock_quantity: Number(item.stockQuantity),
          price_cents: item.price === '' ? null : cents(item.price),
          active: item.active
        }))
    };
    const response = editingProductId
      ? (isPartnerScope
        ? await updatePartnerShopProduct(token, editingProductId, payload)
        : await updateAdminShopProduct(token, editingProductId, payload, scopeOptions))
      : (isPartnerScope
        ? await createPartnerShopProduct(token, payload)
        : await createAdminShopProduct(token, payload, scopeOptions));
    if (response.status === 200 || response.status === 201) {
      const productId = editingProductId || response.json.id;
      if (pendingImages.length > 0) {
        const variantsForImages = editingProductId
          ? (selectedProduct ? (selectedProduct.variants || []) : [])
          : (response.json.variants || []);
        const imageResult = isPartnerScope
          ? await uploadPartnerShopProductImages(
            token,
            productId,
            pendingImages,
            imageLabel,
            resolveVariantId(variantsForImages, imageVariantSku)
          )
          : await uploadAdminShopProductImages(
            token,
            productId,
            pendingImages,
            imageLabel,
            resolveVariantId(variantsForImages, imageVariantSku),
            scopeOptions
          );
        if (imageResult.status !== 200) {
          setMessage(t('SHOP_PRODUCT_SAVED_IMAGE_ERROR', {error: imageResult.json.error || t('ERROR')}));
          setSaving(false);
          await load();
          return;
        }
        pendingImagePreviews.forEach(item => URL.revokeObjectURL(item.url));
        setPendingImages([]);
        setPendingImagePreviews([]);
        setImageVariantSku('');
        setImageLabel('');
        setImageInputKey(value => value + 1);
      }
      await load();
      if (!editingProductId) resetProduct();
      setMessage(t('SHOP_PRODUCT_SAVED'));
    } else {
      setMessage(response.json.error || t('SHOP_PRODUCT_SAVE_ERROR'));
    }
    setSaving(false);
  };

  const removeProduct = async item => {
    if (!window.confirm(t('SHOP_CONFIRM_DELETE_PRODUCT', {name: item.name}))) return;
    const response = isPartnerScope
      ? await deletePartnerShopProduct(token, item.id)
      : await deleteAdminShopProduct(token, item.id, scopeOptions);
    if (response.status === 200) {
      if (editingProductId === item.id) resetProduct();
      await load();
    } else {
      setMessage(response.json.error || t('SHOP_PRODUCT_DELETE_ERROR'));
    }
  };

  const editVariant = item => {
    setEditingVariantId(item.id);
    setVariant({
      size: item.size || '',
      color: item.color || '',
      sku: item.sku || '',
      stockQuantity: item.stock_quantity,
      price: euros(item.price_cents),
      active: item.active
    });
  };

  const saveVariant = async event => {
    event.preventDefault();
    if (!editingProductId) return;
    setSaving(true);
    const payload = {
      size: optional(variant.size),
      color: optional(variant.color),
      sku: buildVariantSku(selectedProduct ? selectedProduct.sku : product.sku, variant.size, variant.color) || variant.sku.trim(),
      stock_quantity: Number(variant.stockQuantity),
      price_cents: variant.price === '' ? null : cents(variant.price),
      active: variant.active
    };
    const response = editingVariantId
      ? (isPartnerScope
        ? await updatePartnerShopVariant(token, editingVariantId, payload)
        : await updateAdminShopVariant(token, editingVariantId, payload, scopeOptions))
      : (isPartnerScope
        ? await createPartnerShopVariant(token, editingProductId, payload)
        : await createAdminShopVariant(token, editingProductId, payload, scopeOptions));
    if (response.status === 200 || response.status === 201) {
      await load();
      setVariant(emptyVariant);
      setEditingVariantId(null);
      setMessage(t('SHOP_VARIANT_SAVED'));
    } else {
      setMessage(response.json.error || t('SHOP_VARIANT_SAVE_ERROR'));
    }
    setSaving(false);
  };

  const removeVariant = async item => {
    if (!window.confirm(t('SHOP_CONFIRM_DELETE_VARIANT', {sku: item.sku}))) return;
    const response = isPartnerScope
      ? await deletePartnerShopVariant(token, item.id)
      : await deleteAdminShopVariant(token, item.id, scopeOptions);
    if (response.status === 200) {
      await load();
      setVariant(emptyVariant);
      setEditingVariantId(null);
    } else {
      setMessage(response.json.error || t('SHOP_VARIANT_DELETE_ERROR'));
    }
  };
  const removeExistingImage = async imageId => {
    if (!editingProductId || !window.confirm(t('SHOP_CONFIRM_REMOVE_IMAGE'))) return;
    const response = isPartnerScope
      ? await deletePartnerShopProductImage(token, editingProductId, imageId)
      : await deleteAdminShopProductImage(token, editingProductId, imageId, scopeOptions);
    if (response.status === 200) {
      await load();
      setMessage(t('SHOP_IMAGE_REMOVED'));
    } else {
      setMessage(response.json.error || t('SHOP_IMAGE_REMOVE_ERROR'));
    }
  };

  const productField = event => setProduct({...product, [event.target.name]: event.target.value});
  const variantField = event => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const next = {...variant, [event.target.name]: value};
    if (event.target.name === 'sku') {
      setVariant(next);
      return;
    }
    next.sku = buildVariantSku(selectedProduct ? selectedProduct.sku : product.sku, next.size, next.color);
    setVariant(next);
  };
  const initialVariantField = (index, event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setInitialVariants(initialVariants.map((item, itemIndex) => {
      if (itemIndex !== index) {
        return item;
      }
      const next = {...item, [event.target.name]: value};
      next.sku = buildVariantSku(product.sku, next.size, next.color);
      return next;
    }));
  };
  const addInitialVariant = () => setInitialVariants([...initialVariants, newEmptyVariant()]);
  const removeInitialVariant = index => setInitialVariants(
    initialVariants.length === 1
      ? [newEmptyVariant()]
      : initialVariants.filter((item, itemIndex) => itemIndex !== index)
  );
  const productSkuField = event => {
    const nextSku = event.target.value;
    setProduct({...product, sku: nextSku});
    setInitialVariants(initialVariants.map(item => ({
      ...item,
      sku: buildVariantSku(nextSku, item.size, item.color)
    })));
  };
  const onPendingImagesChange = event => {
    const files = Array.from(event.target.files || []);
    pendingImagePreviews.forEach(item => URL.revokeObjectURL(item.url));
    setPendingImages(files);
    setPendingImagePreviews(files.map(file => ({
      name: file.name,
      url: URL.createObjectURL(file)
    })));
    if (files.length > 0) {
      window.setTimeout(() => {
        if (imageSectionRef.current) imageSectionRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
      }, 0);
    }
  };
  const chooseImagesForVariant = item => {
    setImageVariantSku(item.sku);
    setImageLabel('');
    if (imageInputRef.current) imageInputRef.current.click();
  };
  const clearPendingImages = () => {
    pendingImagePreviews.forEach(item => URL.revokeObjectURL(item.url));
    setPendingImages([]);
    setPendingImagePreviews([]);
    setImageVariantSku('');
    setImageLabel('');
    setImageInputKey(value => value + 1);
  };

  const publishedProducts = products.filter(item => item.status === 'published').length;
  const lowStockProducts = products.filter(item => Number(item.stock_quantity) <= 5).length;
  const ordersToPrepare = orders.filter(item => item.status === 'paid' || item.status === 'preparing').length;
  const shippedOrders = orders.filter(item => item.status === 'shipped').length;

  return (
    <Fragment>
      <CommonMetaTag/>
      <Fade in appear style={{transition: 'opacity 0.30s linear', marginTop: 100, marginBottom: 100}}>
        <Container className="partner-shop-manager">
          <div className="partner-shop-manager__header d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div className="mb-2">
              <h2 className="mb-1">{title}</h2>
              <div className="text-muted">{t('SHOP_ADMIN_INTRO')}</div>
            </div>
            {shopSection === 'products' &&
              <Button className="partner-shop-manager__new-product" type="button" onClick={startNewProduct}>
                <FaPlus aria-hidden="true"/> {t('SHOP_NEW_PRODUCT')}
              </Button>}
          </div>
          {message && <div className="alert alert-info">{message}</div>}
          <Fragment>
            <Row className="partner-shop-manager__stats mb-3">
              <Col sm={6} xl={3} className="mb-3">
                <Card className="h-100"><Card.Body><FaBoxOpen aria-hidden="true"/><div><div className="text-muted small">{t('SHOP_PUBLISHED_PRODUCTS')}</div><div className="h3 mb-0">{publishedProducts}</div></div></Card.Body></Card>
              </Col>
              <Col sm={6} xl={3} className="mb-3">
                <Card className="h-100"><Card.Body><FaExclamationTriangle aria-hidden="true"/><div><div className="text-muted small">{t('SHOP_LOW_STOCK')}</div><div className="h3 mb-0">{lowStockProducts}</div></div></Card.Body></Card>
              </Col>
              <Col sm={6} xl={3} className="mb-3">
                <Card className="h-100"><Card.Body><FaClipboardList aria-hidden="true"/><div><div className="text-muted small">{t('SHOP_ORDERS_TO_PREPARE')}</div><div className="h3 mb-0">{ordersToPrepare}</div></div></Card.Body></Card>
              </Col>
              <Col sm={6} xl={3} className="mb-3">
                <Card className="h-100"><Card.Body><FaShippingFast aria-hidden="true"/><div><div className="text-muted small">{t('SHOP_SHIPPED_ORDERS')}</div><div className="h3 mb-0">{shippedOrders}</div></div></Card.Body></Card>
              </Col>
            </Row>
            <div className="partner-shop-manager__tabs mb-4" role="tablist" aria-label={t('SHOP_SECTIONS')}>
              <Button
                type="button"
                variant={shopSection === 'products' ? 'primary' : 'link'}
                className={shopSection === 'products' ? 'active' : ''}
                role="tab"
                aria-selected={shopSection === 'products'}
                onClick={() => setShopSection('products')}
              ><FaBoxOpen aria-hidden="true"/> {t('PRODUCTS')}</Button>
              <Button
                type="button"
                variant={shopSection === 'orders' ? 'primary' : 'link'}
                className={shopSection === 'orders' ? 'active' : ''}
                role="tab"
                aria-selected={shopSection === 'orders'}
                onClick={() => setShopSection('orders')}
              ><FaClipboardList aria-hidden="true"/> {t('ORDERS')} {ordersToPrepare > 0 && <Badge variant="danger" className="ml-1">{ordersToPrepare}</Badge>}</Button>
              <Button
                type="button"
                variant={shopSection === 'settings' ? 'primary' : 'link'}
                className={shopSection === 'settings' ? 'active' : ''}
                role="tab"
                aria-selected={shopSection === 'settings'}
                onClick={() => setShopSection('settings')}
              ><FaCog aria-hidden="true"/> {t('SETTINGS')}</Button>
            </div>
          </Fragment>
          {shopSection === 'settings' && <Card className="mb-4">
            <Card.Body>
              <h4>{t('SHOP_ORDER_DELIVERY_SETTINGS')}</h4>
              <p className="text-muted">
                {t('SHOP_ORDER_DELIVERY_SETTINGS_HELP', {shop: settings.shop_name || ''})}
              </p>
              <Form onSubmit={saveSettings}>
                <Form.Group>
                  <Form.Label>{t('SHOP_ORDER_EMAIL')}</Form.Label>
                  <Form.Control
                    type="email"
                    value={settingsDraft.orderNotificationEmail}
                    onChange={event => setSettingsDraft({...settingsDraft, orderNotificationEmail: event.target.value})}
                    placeholder={t('SHOP_ORDER_EMAIL_PLACEHOLDER')}
                  />
                  <Form.Text className="text-muted">
                    {t('SHOP_ORDER_EMAIL_HELP')}
                  </Form.Text>
                </Form.Group>
                <Card className="mb-3"><Card.Body>
                  <Form.Check
                    type="switch"
                    id="shop-settings-shipping"
                    label={t('SHOP_ALLOW_SHIPPING')}
                    checked={settingsDraft.shippingEnabled}
                    onChange={event => setSettingsDraft({...settingsDraft, shippingEnabled: event.target.checked})}
                  />
                  {settingsDraft.shippingEnabled && <Row className="mt-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('SHOP_FIXED_SHIPPING_COST')}</Form.Label>
                        <Form.Control type="number" min="0" step="0.01" value={settingsDraft.shippingCost}
                          onChange={event => setSettingsDraft({...settingsDraft, shippingCost: event.target.value})}/>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('SHOP_FREE_SHIPPING_FROM')}</Form.Label>
                        <Form.Control type="number" min="0" step="0.01" value={settingsDraft.freeShippingThreshold}
                          onChange={event => setSettingsDraft({...settingsDraft, freeShippingThreshold: event.target.value})}
                          placeholder={t('SHOP_LEAVE_EMPTY_TO_DISABLE')}/>
                      </Form.Group>
                    </Col>
                  </Row>}
                </Card.Body></Card>
                <Card className="mb-3"><Card.Body>
                  <Form.Check
                    type="switch"
                    id="shop-settings-pickup"
                    label={t('SHOP_ALLOW_PICKUP')}
                    checked={settingsDraft.pickupEnabled}
                    onChange={event => setSettingsDraft({...settingsDraft, pickupEnabled: event.target.checked})}
                  />
                  <Form.Text className="text-muted">{t('SHOP_PICKUP_HELP')}</Form.Text>
                </Card.Body></Card>
                {!settingsDraft.shippingEnabled && !settingsDraft.pickupEnabled &&
                  <div className="alert alert-warning">{t('SHOP_DELIVERY_METHOD_REQUIRED')}</div>}
                <Button type="submit" disabled={saving || settingsLoading}>
                  {saving ? t('SAVING') : t('SAVE_SETTINGS')}
                </Button>{' '}
                <Button type="button" variant="secondary" onClick={loadSettings} disabled={settingsLoading}>{t('CANCEL_CHANGES')}</Button>
              </Form>
            </Card.Body>
          </Card>}
          {shopSection === 'orders' && <Card className="mb-4">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <h4 className="mb-2">{displayedOrdersTitle}</h4>
                <div className="d-flex align-items-center">
                  <Form.Control
                    as="select"
                    size="sm"
                    className="mr-2"
                    value={orderFilter}
                    onChange={event => setOrderFilter(event.target.value)}
                  >
                    <option value="">{t('ALL_STATUSES')}</option>
                    {Object.keys(orderStatusKeys).map(value =>
                      <option value={value} key={value}>{orderStatusLabel(value)}</option>)}
                  </Form.Control>
                  <Button size="sm" variant="secondary" onClick={loadOrders} disabled={ordersLoading}>
                    {ordersLoading ? t('LOADING') : t('RELOAD')}
                  </Button>
                </div>
              </div>
              {orders.length === 0 ? <p className="mb-0">{t('SHOP_NO_ORDERS')}</p> :
                <Table responsive hover>
                  <thead><tr><th>{t('ORDER')}</th><th>{t('CUSTOMER')}</th><th>{t('DATE')}</th><th>{t('TOTAL')}</th><th>{t('STATE')}</th><th></th></tr></thead>
                  <tbody>{orders.map(item =>
                    <tr key={item.id}>
                      <td><strong>{item.number}</strong><br/><small>{t('SHOP_ORDER_LINES', {count: item.items.length})}</small></td>
                      <td>
                        {item.customer_name || '-'}<br/>
                        <small>{item.customer_email}</small>
                        {item.delivery_method === 'pickup' ? (
                          <div><small className="text-muted">{t('SHOP_PICKUP')}</small></div>
                        ) : orderDeliveryLine(item) && (
                          <div><small className="text-muted">{t('DELIVERY')}: {orderDeliveryLine(item)}</small></div>
                        )}
                      </td>
                      <td>{item.created_at ? new Date(item.created_at).toLocaleString(i18n.language) : '-'}</td>
                      <td>EUR {euros(item.total_cents)}</td>
                      <td><Badge variant={statusBadgeVariant(item.status)}>{orderStatusLabel(item.status)}</Badge></td>
                      <td><Button size="sm" onClick={() => selectOrder(item)}>{t('MANAGE')}</Button></td>
                    </tr>)}</tbody>
                </Table>}
              {selectedOrderId && (() => {
                const selectedOrder = orders.find(item => item.id === selectedOrderId);
                if (!selectedOrder) return null;
                return (
                  <Card className="mt-3">
                    <Card.Body>
                      <h5>{selectedOrder.number} - {selectedOrder.customer_name || selectedOrder.customer_email}</h5>
                      <p className="mb-2">{selectedOrder.customer_email}{selectedOrder.customer_phone ? ` - ${selectedOrder.customer_phone}` : ''}</p>
                      <Card className="mb-3">
                        <Card.Body className="py-2">
                          <strong>{selectedOrder.delivery_method === 'pickup' ? t('DELIVERY_METHOD') : t('DELIVERY_ADDRESS')}</strong>
                          {selectedOrder.delivery_method === 'pickup' ? (
                            <div className="mt-1">{t('SHOP_FREE_PICKUP')}</div>
                          ) : orderDeliveryBlock(selectedOrder, t).length > 0 ? (
                            <div className="mt-1">
                              {orderDeliveryBlock(selectedOrder, t).map((line, index) => (
                                <div key={`${line}-${index}`}>{line}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-muted mt-1">{t('SHOP_NOT_AVAILABLE_FOR_ORDER')}</div>
                          )}
                        </Card.Body>
                      </Card>
                      <Table responsive size="sm">
                        <thead><tr><th>{t('ITEM')}</th><th>SKU</th><th>{t('QUANTITY')}</th><th>{t('TOTAL')}</th></tr></thead>
                        <tbody>{selectedOrder.items.map(item =>
                          <tr key={item.id}>
                            <td>{item.product_name}{item.size ? ` - ${item.size}` : ''}{item.color ? ` - ${item.color}` : ''}</td>
                            <td>{item.variant_sku || item.product_sku}</td>
                            <td>{item.quantity}</td>
                            <td>EUR {euros(item.line_total_cents)}</td>
                          </tr>)}</tbody>
                      </Table>
                      <div className="text-right mb-3">
                        <div>{t('ITEMS')}: EUR {euros(selectedOrder.subtotal_cents)}</div>
                        <div>{t('SHIPPING')}: EUR {euros(selectedOrder.shipping_cents)}</div>
                        <strong>{t('TOTAL')}: EUR {euros(selectedOrder.total_cents)}</strong>
                      </div>
                      <Form onSubmit={saveOrder}>
                        <Row>
                          <Col md={4}><Form.Group><Form.Label>{t('STATE')}</Form.Label><Form.Control as="select" value={orderDraft.status} onChange={event => setOrderDraft({...orderDraft, status: event.target.value})}>{Object.keys(orderStatusKeys).map(value => <option value={value} key={value}>{orderStatusLabel(value)}</option>)}</Form.Control></Form.Group></Col>
                          <Col md={4}><Form.Group><Form.Label>{t('TRACKING_CODE')}</Form.Label><Form.Control disabled={selectedOrder.delivery_method === 'pickup'} value={orderDraft.tracking_code} onChange={event => setOrderDraft({...orderDraft, tracking_code: event.target.value})}/></Form.Group></Col>
                          <Col md={4}><Form.Group><Form.Label>{t('STATUS_CHANGE_NOTE')}</Form.Label><Form.Control value={orderDraft.note} onChange={event => setOrderDraft({...orderDraft, note: event.target.value})}/></Form.Group></Col>
                        </Row>
                        <Form.Group><Form.Label>{t('INTERNAL_NOTE')}</Form.Label><Form.Control as="textarea" rows={2} value={orderDraft.internal_note} onChange={event => setOrderDraft({...orderDraft, internal_note: event.target.value})}/></Form.Group>
                        <Button size="sm" type="submit" disabled={saving}>{t('SAVE_ORDER')}</Button>{' '}
                        <Button size="sm" type="button" variant="secondary" onClick={() => setSelectedOrderId(null)}>{t('CLOSE_BUTTON')}</Button>
                      </Form>
                    </Card.Body>
                  </Card>
                );
              })()}
            </Card.Body>
          </Card>}
          {shopSection === 'products' && <Row>
            {productEditorOpen && <Col lg={5}>
              <Card className="mb-4">
                <Card.Body>
                  <h4>{editingProductId ? t('EDIT_PRODUCT') : t('SHOP_NEW_PRODUCT')}</h4>
                  <Form onSubmit={saveProduct}>
                    <Form.Group><Form.Label>{t('NAME')} *</Form.Label><Form.Control required name="name" value={product.name} onChange={productField}/></Form.Group>
                    <Form.Group><Form.Label>{t('PRODUCT_SKU')} *</Form.Label><Form.Control required name="sku" value={product.sku} onChange={productSkuField}/></Form.Group>
                    <Form.Group><Form.Label>{t('CATEGORY')}</Form.Label><Form.Control name="category" value={product.category} onChange={productField}/></Form.Group>
                    <Row>
                      <Col><Form.Group><Form.Label>{t('PRICE')} EUR *</Form.Label><Form.Control required min="0" step="0.01" type="number" name="price" value={product.price} onChange={productField}/></Form.Group></Col>
                      <Col><Form.Group><Form.Label>{t('DISCOUNTED_PRICE')}</Form.Label><Form.Control min="0" step="0.01" type="number" name="salePrice" value={product.salePrice} onChange={productField}/></Form.Group></Col>
                    </Row>
                    <div ref={imageSectionRef}>
                    <Card className="mb-4 border-primary">
                      <Card.Body>
                      <div className="mb-3">
                        <h5 className="mb-1">{t('PRODUCT_PHOTOS')}</h5>
                        <div className="text-muted small">
                          {t('PRODUCT_PHOTOS_HELP')}
                        </div>
                      </div>
                      {editingProductId && selectedProductImages.length > 0 &&
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>{t('PUBLISHED_PHOTOS')}</strong>
                            <small className="text-muted">{t('REPLACE_PHOTO_HELP')}</small>
                          </div>
                          <Row>
                            {selectedProductImages.map(image => (
                              <Col md={6} key={image.id} className="mb-3">
                                <Card className="h-100">
                                  <img
                                    src={`${Configuration.apiEndPoint}/shop/products/${editingProductId}/images/${image.id}`}
                                    alt={image.label || image.variant_sku || image.filename}
                                    style={{width: '100%', height: 160, objectFit: 'cover'}}
                                  />
                                  <Card.Body className="p-2">
                                    <Badge variant="success" className="mb-2">{t('PUBLISHED')}</Badge>
                                    <div className="small">
                                      <strong>{image.label || image.filename}</strong>
                                      <div className="text-muted">
                                        {image.variant_sku ? `${t('VARIANT')}: ${image.variant_sku}` : t('VALID_FOR_WHOLE_PRODUCT')}
                                      </div>
                                    </div>
                                    <Button size="sm" variant="outline-danger" className="mt-2" onClick={() => removeExistingImage(image.id)}>{t('REMOVE_PHOTO')}</Button>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </div>}
                      {pendingImagePreviews.length > 0 &&
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>{t('NEW_SELECTED_PHOTOS')}</strong>
                            <Button size="sm" variant="outline-secondary" type="button" onClick={clearPendingImages}>{t('MYVIDEO_CANCEL_SELECTION')}</Button>
                          </div>
                          <Row>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label>{t('PHOTOS_BELONG_TO')}</Form.Label>
                                <Form.Control as="select" value={imageVariantSku} onChange={event => setImageVariantSku(event.target.value)}>
                                  <option value="">{t('WHOLE_PRODUCT_RECOMMENDED')}</option>
                                  {currentImageVariantOptions.map(item => (
                                    <option key={item.id || item.sku} value={item.sku}>
                                      {[item.color, item.size].filter(Boolean).join(' - ') || t('VARIANT')} ({item.sku})
                                    </option>
                                  ))}
                                </Form.Control>
                                <Form.Text className="text-muted">{t('PHOTO_ASSOCIATION_HELP')}</Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label>{t('PHOTO_DESCRIPTION_OPTIONAL')}</Form.Label>
                                <Form.Control value={imageLabel} onChange={event => setImageLabel(event.target.value)} placeholder={t('PHOTO_DESCRIPTION_EXAMPLE')} />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            {pendingImagePreviews.map((image, index) => (
                              <Col md={6} key={`${image.name}-${index}`} className="mb-3">
                                <Card className="h-100 border-warning">
                                  <img
                                    src={image.url}
                                    alt={image.name}
                                    style={{width: '100%', height: 160, objectFit: 'cover'}}
                                  />
                                  <Card.Body className="p-2">
                                    <Badge variant="warning" className="mb-2">{t('TO_BE_SAVED')}</Badge>
                                    <div className="small"><strong>{image.name}</strong></div>
                                    <div className="small text-muted">
                                      {imageVariantSku ? `${t('VARIANT')}: ${imageVariantSku}` : t('VALID_FOR_WHOLE_PRODUCT')}
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </div>}
                      <div className="text-center border rounded p-4">
                        <div className="mb-2"><strong>{pendingImages.length > 0 ? t('CHOOSE_MORE_PHOTOS') : t('ADD_PRODUCT_PHOTOS')}</strong></div>
                        <div className="text-muted small mb-3">{t('PRODUCT_PHOTO_FORMAT_HELP')}</div>
                        <label className="btn btn-primary mb-0" htmlFor="shop-product-images">
                          {pendingImages.length > 0 ? t('CHANGE_SELECTED_PHOTOS') : t('CHOOSE_PHOTOS')}
                        </label>
                        <Form.Control
                          id="shop-product-images"
                          key={imageInputKey}
                          ref={imageInputRef}
                          className="d-none"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          onChange={onPendingImagesChange}
                        />
                      </div>
                      {pendingImages.length > 0 &&
                        <div className="alert alert-warning mt-3 mb-0">
                          {t('PHOTOS_UPLOAD_ON_SAVE')} <strong>{editingProductId ? t('UPDATE') : t('CREATE_PRODUCT')}</strong>.
                        </div>}
                      </Card.Body>
                    </Card>
                    </div>
                    <Row>
                      <Col><Form.Group><Form.Label>{t('STATE')}</Form.Label><Form.Control as="select" name="status" value={product.status} onChange={productField}><option value="draft">{t('SHOP_PRODUCT_DRAFT')}</option><option value="published">{t('SHOP_PRODUCT_PUBLISHED')}</option><option value="sold_out">{t('SHOP_PRODUCT_SOLD_OUT')}</option><option value="archived">{t('SHOP_PRODUCT_ARCHIVED')}</option></Form.Control></Form.Group></Col>
                      <Col><Form.Group><Form.Label>{t('WEIGHT_GRAMS')}</Form.Label><Form.Control min="0" type="number" name="weightGrams" value={product.weightGrams} onChange={productField}/></Form.Group></Col>
                    </Row>
                    <Form.Group><Form.Label>{t('DESCRIPTION')}</Form.Label><Form.Control as="textarea" rows={4} name="description" value={product.description} onChange={productField}/></Form.Group>
                    {!editingProductId &&
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>{t('SHOP_INITIAL_VARIANTS')}</strong>
                          <Button size="sm" type="button" variant="secondary" onClick={addInitialVariant}>{t('ADD_ROW')}</Button>
                        </div>
                        <small className="d-block mb-2">{t('SHOP_INITIAL_VARIANTS_HELP')}</small>
                        {initialVariants.map((item, index) =>
                          <Card className="mb-2" key={index}>
                            <Card.Body className="p-2">
                              <Row>
                                <Col sm={6}><Form.Group><Form.Label>{t('VARIANT_SKU')}</Form.Label><Form.Control readOnly name="sku" value={item.sku} onChange={event => initialVariantField(index, event)}/></Form.Group></Col>
                                <Col sm={3}><Form.Group><Form.Label>{t('SIZE')}</Form.Label><Form.Control name="size" value={item.size} onChange={event => initialVariantField(index, event)}/></Form.Group></Col>
                                <Col sm={3}><Form.Group><Form.Label>{t('COLOR')}</Form.Label><Form.Control name="color" value={item.color} onChange={event => initialVariantField(index, event)}/></Form.Group></Col>
                              </Row>
                              <Row>
                                <Col sm={4}><Form.Group><Form.Label>{t('QUANTITY')}</Form.Label><Form.Control min="0" required={Boolean(item.sku.trim())} type="number" name="stockQuantity" value={item.stockQuantity} onChange={event => initialVariantField(index, event)}/></Form.Group></Col>
                                <Col sm={4}><Form.Group><Form.Label>{t('OPTIONAL_PRICE')}</Form.Label><Form.Control min="0" step="0.01" type="number" name="price" value={item.price} onChange={event => initialVariantField(index, event)}/></Form.Group></Col>
                                <Col sm={2}><Form.Group><Form.Check className="mt-4" label={t('ACTIVE')} name="active" checked={item.active} onChange={event => initialVariantField(index, event)}/></Form.Group></Col>
                                <Col sm={2} className="d-flex align-items-end"><Button className="mb-3" size="sm" type="button" variant="danger" onClick={() => removeInitialVariant(index)}>{t('REMOVE')}</Button></Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        )}
                      </div>}
                    <Button type="submit" disabled={saving}>{editingProductId ? t('UPDATE') : t('CREATE_PRODUCT')}</Button>{' '}
                    {editingProductId && <Button variant="secondary" onClick={resetProduct}>{t('CANCEL')}</Button>}
                  </Form>
                </Card.Body>
              </Card>
            </Col>}
            <Col lg={!productEditorOpen ? 12 : 7}>
              <Card className="mb-4">
                <Card.Body>
                  <div className="d-flex justify-content-between mb-3"><h4>{t('PRODUCTS')}</h4><Button size="sm" variant="secondary" onClick={load} disabled={loading}>{loading ? t('LOADING') : t('RELOAD')}</Button></div>
                  {products.length === 0 ? <p>{t('SHOP_NO_PRODUCTS')}</p> :
                    <Table responsive striped>
                      <thead><tr><th>{t('PRODUCT')}</th><th>{t('PRICE')}</th><th>{t('STATE')}</th><th>{t('STOCK')}</th><th></th></tr></thead>
                      <tbody>{products.map(item =>
                        <tr key={item.id}>
                          <td><strong>{item.name}</strong><br/><small>{item.sku}</small></td>
                          <td>EUR {euros(item.price_cents)}</td><td><Badge variant={statusBadgeVariant(item.status)}>{productStatusLabel(item.status)}</Badge></td><td>{item.stock_quantity}</td>
                          <td><Button size="sm" onClick={() => editProduct(item)}>{t('EDIT')}</Button>{' '}<Button size="sm" variant="danger" onClick={() => removeProduct(item)}>{t('DELETE')}</Button></td>
                        </tr>)}</tbody>
                    </Table>}
                </Card.Body>
              </Card>
              {selectedProduct &&
                <Card>
                  <Card.Body>
                    <h4>{t('VARIANTS')}: {selectedProduct.name}</h4>
                    <Form onSubmit={saveVariant}>
                      <Row>
                        <Col md={4}><Form.Group><Form.Label>SKU *</Form.Label><Form.Control readOnly required name="sku" value={variant.sku} onChange={variantField}/></Form.Group></Col>
                        <Col md={4}><Form.Group><Form.Label>{t('SIZE')}</Form.Label><Form.Control name="size" value={variant.size} onChange={variantField}/></Form.Group></Col>
                        <Col md={4}><Form.Group><Form.Label>{t('COLOR')}</Form.Label><Form.Control name="color" value={variant.color} onChange={variantField}/></Form.Group></Col>
                      </Row>
                      <Row>
                        <Col md={4}><Form.Group><Form.Label>{t('AVAILABILITY')}</Form.Label><Form.Control required min="0" type="number" name="stockQuantity" value={variant.stockQuantity} onChange={variantField}/></Form.Group></Col>
                        <Col md={4}><Form.Group><Form.Label>{t('VARIANT_PRICE')} EUR</Form.Label><Form.Control min="0" step="0.01" type="number" name="price" value={variant.price} onChange={variantField}/></Form.Group></Col>
                        <Col md={4}><Form.Group><Form.Check className="mt-4" label={t('ACTIVE')} name="active" checked={variant.active} onChange={variantField}/></Form.Group></Col>
                      </Row>
                      <Button size="sm" type="submit" disabled={saving}>{editingVariantId ? t('UPDATE_VARIANT') : t('ADD_VARIANT')}</Button>{' '}
                      {editingVariantId && <Button size="sm" variant="secondary" onClick={() => { setVariant(emptyVariant); setEditingVariantId(null); }}>{t('CANCEL')}</Button>}
                    </Form>
                    <Table responsive className="mt-3">
                      <thead><tr><th>SKU</th><th>{t('SIZE')}</th><th>{t('COLOR')}</th><th>{t('STOCK')}</th><th>{t('ACTIVE')}</th><th></th></tr></thead>
                      <tbody>{selectedProduct.variants.map(item =>
                        <tr key={item.id}>
                          <td>{item.sku}</td><td>{item.size || '-'}</td><td>{item.color || '-'}</td><td>{item.stock_quantity}</td><td>{item.active ? t('YES') : t('NO')}</td>
                          <td>
                            <Button size="sm" variant="outline-primary" onClick={() => chooseImagesForVariant(item)}>{t('ADD_PHOTO')}</Button>{' '}
                            <Button size="sm" onClick={() => editVariant(item)}>{t('EDIT')}</Button>{' '}
                            <Button size="sm" variant="danger" onClick={() => removeVariant(item)}>{t('DELETE')}</Button>
                          </td>
                        </tr>)}</tbody>
                    </Table>
                  </Card.Body>
                </Card>}
            </Col>
          </Row>}
        </Container>
      </Fade>
    </Fragment>
  );
};

export default AdminShop;
